import React, { useState, useEffect, useCallback } from 'react';
import { usePractice } from '../context/PracticeContext';
import api from '../services/api';
import Tooltip from '../components/Tooltip';
import {
  ComposedChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend
} from 'recharts';

/**
 * Reputation Management Dashboard
 * Tabs: Dashboard, Feedback, Locations, Platforms, Settings
 */
function Reputation() {
  const { selectedPracticeId } = usePractice();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);

  // Feedback state
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackFilter, setFeedbackFilter] = useState('NEEDS_REVIEW');

  // Locations state
  const [locations, setLocations] = useState([]);
  const [locationWeightValid, setLocationWeightValid] = useState(true);

  // Platforms state
  const [platforms, setPlatforms] = useState([]);
  const [platformWeightValid, setPlatformWeightValid] = useState(true);

  // Date range state for dashboard trends
  const [dateRange, setDateRange] = useState('30d');

  // Config state
  const [config, setConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  // Inline editing state for locations/platforms
  const [editingLocation, setEditingLocation] = useState(null); // null | 'new' | location object
  const [editingPlatform, setEditingPlatform] = useState(null); // null | 'new' | platform object

  const loadDashboard = useCallback(async () => {
    if (!selectedPracticeId) return;
    try {
      setLoading(true);
      const now = new Date();
      const days = dateRange === '7d' ? 7 : dateRange === '90d' ? 90 : 30;
      const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      const response = await api.getReputationDashboard(selectedPracticeId, from.toISOString(), now.toISOString());
      if (response.success) {
        setDashboardData(response.data);
      } else {
        setError(response.error || 'Failed to load dashboard');
      }
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedPracticeId, dateRange]);

  const loadFeedback = useCallback(async () => {
    if (!selectedPracticeId) return;
    try {
      const response = await api.listFeedbackResponses(selectedPracticeId, {
        status: feedbackFilter !== 'ALL' ? feedbackFilter : undefined,
        maxRating: feedbackFilter === 'LOW_RATINGS' ? 3 : undefined
      });
      if (response.success) {
        setFeedbackList(response.data);
      }
    } catch (err) {
      console.error('Failed to load feedback', err);
    }
  }, [selectedPracticeId, feedbackFilter]);

  const loadLocations = useCallback(async () => {
    if (!selectedPracticeId) return;
    try {
      const response = await api.listLocations(selectedPracticeId);
      if (response.success) {
        setLocations(response.data);
        setLocationWeightValid(response.weightValidation?.valid ?? true);
      }
    } catch (err) {
      console.error('Failed to load locations', err);
    }
  }, [selectedPracticeId]);

  const loadPlatforms = useCallback(async () => {
    if (!selectedPracticeId) return;
    try {
      const response = await api.listPlatforms(selectedPracticeId);
      if (response.success) {
        setPlatforms(response.data);
        setPlatformWeightValid(response.weightValidation?.valid ?? true);
      }
    } catch (err) {
      console.error('Failed to load platforms', err);
    }
  }, [selectedPracticeId]);

  const loadConfig = useCallback(async () => {
    if (!selectedPracticeId) return;
    try {
      const response = await api.getReputationConfig(selectedPracticeId);
      if (response.success) {
        setConfig(response.data);
      }
    } catch (err) {
      console.error('Failed to load config', err);
    }
  }, [selectedPracticeId]);

  useEffect(() => {
    if (activeTab === 'dashboard') loadDashboard();
    if (activeTab === 'feedback') loadFeedback();
    if (activeTab === 'locations') loadLocations();
    if (activeTab === 'platforms') loadPlatforms();
    if (activeTab === 'settings') loadConfig();
  }, [activeTab, selectedPracticeId, loadDashboard, loadFeedback, loadLocations, loadPlatforms, loadConfig]);

  const handleUpdateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      await api.updateFeedbackStatus(selectedPracticeId, feedbackId, newStatus);
      loadFeedback();
    } catch (err) {
      console.error('Failed to update feedback status', err);
    }
  };

  const handleSaveConfig = async () => {
    if (!config) return;
    try {
      setSavingConfig(true);
      await api.updateReputationConfig(selectedPracticeId, config);
      alert('Settings saved successfully');
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      await api.deleteLocation(selectedPracticeId, locationId);
      loadLocations();
    } catch (err) {
      console.error('Failed to delete location', err);
    }
  };

  const handleDeletePlatform = async (platformId) => {
    if (!window.confirm('Are you sure you want to delete this platform?')) return;
    try {
      await api.deletePlatform(selectedPracticeId, platformId);
      loadPlatforms();
    } catch (err) {
      console.error('Failed to delete platform', err);
    }
  };

  if (!selectedPracticeId) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>Please select a practice to view reputation management.</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.pageTitle}>Reputation Management</h1>

      {/* Tabs */}
      <div style={styles.tabs}>
        {['dashboard', 'feedback', 'locations', 'platforms', 'settings'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.activeTab : {})
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {activeTab === 'dashboard' && (
          <DashboardTab data={dashboardData} loading={loading} error={error} dateRange={dateRange} onDateRangeChange={setDateRange} onRetry={loadDashboard} />
        )}

        {activeTab === 'feedback' && (
          <FeedbackTab
            feedback={feedbackList}
            filter={feedbackFilter}
            onFilterChange={setFeedbackFilter}
            onUpdateStatus={handleUpdateFeedbackStatus}
            practiceId={selectedPracticeId}
          />
        )}

        {activeTab === 'locations' && (
          <LocationsTab
            locations={locations}
            weightValid={locationWeightValid}
            onAdd={() => setEditingLocation('new')}
            onEdit={(loc) => setEditingLocation(loc)}
            onDelete={handleDeleteLocation}
            onRefresh={loadLocations}
            practiceId={selectedPracticeId}
            editing={editingLocation}
            onCancelEdit={() => setEditingLocation(null)}
          />
        )}

        {activeTab === 'platforms' && (
          <PlatformsTab
            platforms={platforms}
            weightValid={platformWeightValid}
            onAdd={() => setEditingPlatform('new')}
            onEdit={(plat) => setEditingPlatform(plat)}
            onDelete={handleDeletePlatform}
            onRefresh={loadPlatforms}
            practiceId={selectedPracticeId}
            editing={editingPlatform}
            onCancelEdit={() => setEditingPlatform(null)}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            config={config}
            onChange={setConfig}
            onSave={handleSaveConfig}
            saving={savingConfig}
          />
        )}
      </div>
    </div>
  );
}

// Aggregate daily data into weekly or monthly buckets
function aggregateTrendData(dailyData, granularity) {
  if (!dailyData || dailyData.length === 0) return [];
  if (granularity === 'daily') return dailyData;

  const buckets = {};
  dailyData.forEach(item => {
    // Normalize: PostgreSQL DATE() serializes as "2026-03-12T00:00:00.000Z"
    const dateStr = String(item.date || '').split('T')[0];
    if (!dateStr) return;

    let key;
    if (granularity === 'weekly') {
      const d = new Date(dateStr + 'T00:00:00');
      if (isNaN(d.getTime())) return;
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(d);
      monday.setDate(diff);
      key = monday.toISOString().split('T')[0];
    } else {
      key = dateStr.substring(0, 7);
    }
    if (!buckets[key]) {
      buckets[key] = { date: key, count: 0, _ratingSum: 0, _ratingCount: 0,
        low_count: 0, high_count: 0, review_clicks: 0,
        total: 0, submitted: 0, suppressed: 0, expired: 0, click_count: 0 };
    }
    const b = buckets[key];
    if (item.count !== undefined) b.count += item.count;
    if (item.avg_rating !== undefined && item.count > 0) {
      b._ratingSum += item.avg_rating * item.count;
      b._ratingCount += item.count;
    }
    if (item.low_count !== undefined) b.low_count += item.low_count;
    if (item.high_count !== undefined) b.high_count += item.high_count;
    if (item.review_clicks !== undefined) b.review_clicks += item.review_clicks;
    if (item.total !== undefined) b.total += item.total;
    if (item.submitted !== undefined) b.submitted += item.submitted;
    if (item.suppressed !== undefined) b.suppressed += item.suppressed;
    if (item.expired !== undefined) b.expired += item.expired;
    if (item.click_count !== undefined) b.click_count += item.click_count;
  });

  return Object.values(buckets).map(b => ({
    ...b,
    avg_rating: b._ratingCount > 0 ? parseFloat((b._ratingSum / b._ratingCount).toFixed(2)) : 0
  })).sort((a, c) => a.date.localeCompare(c.date));
}

// Format date label for chart X-axis
function formatDateLabel(dateStr, granularity) {
  if (!dateStr) return '';
  const str = String(dateStr).split('T')[0];
  if (granularity === 'monthly') {
    const [y, m] = str.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
  }
  const d = new Date(str + 'T00:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

// Dashboard Tab Component
function DashboardTab({ data, loading, error, dateRange, onDateRangeChange, onRetry }) {
  const [granularity, setGranularity] = useState('daily');

  if (loading) {
    return <div style={styles.loading}>Loading dashboard...</div>;
  }

  if (error || !data) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>&#9888;</div>
        <p style={{ color: '#dc2626', fontSize: 15, fontWeight: 500, margin: '0 0 8px' }}>{error || 'No dashboard data available'}</p>
        <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 16px' }}>This may happen if reputation tracking is not yet configured for this practice.</p>
        <button onClick={onRetry} style={{ padding: '8px 20px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  const { summary, rating_distribution, by_location, by_platform, trends } = data;

  const responseTrend = aggregateTrendData(trends?.responses || [], granularity);
  const requestTrend = aggregateTrendData(trends?.requests || [], granularity);
  const clickTrend = aggregateTrendData(trends?.clicks || [], granularity);

  return (
    <div>
      {/* Date Range + Granularity Controls */}
      <div style={styles.controlBar}>
        <div style={styles.buttonGroup}>
          {[['7d', '7 Days'], ['30d', '30 Days'], ['90d', '90 Days']].map(([val, label]) => (
            <button key={val} onClick={() => onDateRangeChange(val)}
              style={{ ...styles.rangeButton, ...(dateRange === val ? styles.rangeButtonActive : {}) }}>
              {label}
            </button>
          ))}
        </div>
        <div style={styles.buttonGroup}>
          {['daily', 'weekly', 'monthly'].map(g => (
            <button key={g} onClick={() => setGranularity(g)}
              style={{ ...styles.rangeButton, ...(granularity === g ? styles.rangeButtonActive : {}) }}>
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{summary.total_responses}</div>
          <div style={styles.summaryLabel}>Total Responses <Tooltip text="Total feedback responses received from patients" /></div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{summary.average_rating || 'N/A'}</div>
          <div style={styles.summaryLabel}>Average Rating <Tooltip text="Average star rating across all feedback" /></div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{summary.review_clicks}</div>
          <div style={styles.summaryLabel}>Review Clicks <Tooltip text="Times patients clicked through to leave a public review" /></div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{(summary.click_through_rate * 100).toFixed(0)}%</div>
          <div style={styles.summaryLabel}>Click-through Rate <Tooltip text="Percentage of high-rating patients who clicked the review link" /></div>
        </div>
      </div>

      {/* Chart 1: Rating Trend */}
      {responseTrend.length > 0 && (
        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Rating Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={responseTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => formatDateLabel(v, granularity)} />
              <YAxis yAxisId="left" domain={[0, 5]} tick={{ fontSize: 11 }} label={{ value: 'Rating', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6b7280' } }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} label={{ value: 'Responses', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#6b7280' } }} />
              <RechartsTooltip />
              <Legend />
              <Bar yAxisId="right" dataKey="count" fill="#93c5fd" name="Responses" radius={[3, 3, 0, 0]} />
              <Line yAxisId="left" type="monotone" dataKey="avg_rating" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} name="Avg Rating" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Chart 2: Feedback Volume */}
      {requestTrend.length > 0 && (
        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Feedback Volume</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={requestTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => formatDateLabel(v, granularity)} />
              <YAxis tick={{ fontSize: 11 }} />
              <RechartsTooltip />
              <Legend />
              <Area type="monotone" dataKey="total" fill="#dbeafe" stroke="#2563eb" fillOpacity={0.3} name="Requests Sent" />
              <Area type="monotone" dataKey="submitted" fill="#d1fae5" stroke="#10b981" fillOpacity={0.3} name="Responses" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Chart 3: Review Clicks */}
      {clickTrend.length > 0 && (
        <div style={styles.chartCard}>
          <h3 style={styles.sectionTitle}>Review Clicks</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={clickTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => formatDateLabel(v, granularity)} />
              <YAxis tick={{ fontSize: 11 }} />
              <RechartsTooltip />
              <Bar dataKey="click_count" fill="#10b981" name="Review Clicks" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Rating Distribution */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Rating Distribution</h3>
        <div style={styles.ratingBars}>
          {[5, 4, 3, 2, 1].map(rating => {
            const count = rating_distribution[rating] || 0;
            const total = Object.values(rating_distribution).reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? (count / total) * 100 : 0;
            return (
              <div key={rating} style={styles.ratingBar}>
                <span style={styles.ratingLabel}>{rating} ★</span>
                <div style={styles.barContainer}>
                  <div
                    style={{
                      ...styles.barFill,
                      width: `${percentage}%`,
                      backgroundColor: rating >= 4 ? '#10b981' : rating === 3 ? '#f59e0b' : '#ef4444'
                    }}
                  />
                </div>
                <span style={styles.ratingCount}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Low Ratings Alert */}
      {summary.low_rating_count > 0 && (
        <div style={styles.alertBox}>
          <strong>{summary.low_rating_count}</strong> low rating(s) need review
        </div>
      )}

      {/* Distribution by Location & Platform */}
      <div style={styles.distributionGrid}>
        {by_location.length > 0 && (
          <div style={styles.distributionCard}>
            <h4 style={styles.distributionTitle}>By Location</h4>
            {by_location.map(loc => (
              <div key={loc.location_id} style={styles.distributionItem}>
                <span>{loc.location_name || 'Unknown'}</span>
                <span>{loc.click_count} ({loc.percentage}%)</span>
              </div>
            ))}
          </div>
        )}
        {by_platform.length > 0 && (
          <div style={styles.distributionCard}>
            <h4 style={styles.distributionTitle}>By Platform</h4>
            {by_platform.map(plat => (
              <div key={plat.platform} style={styles.distributionItem}>
                <span>{plat.platform}</span>
                <span>{plat.click_count} ({plat.percentage}%)</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Feedback Tab Component
function FeedbackTab({ feedback, filter, onFilterChange, onUpdateStatus, practiceId }) {
  const statusColors = {
    NONE: '#9ca3af',
    NEEDS_REVIEW: '#ef4444',
    REVIEWED: '#f59e0b',
    RESOLVED: '#10b981'
  };

  const [aiDraft, setAiDraft] = useState(null); // { feedbackId, response, tone, loading, editing }
  const [editedResponse, setEditedResponse] = useState('');

  // Manual feedback trigger state
  const [feedbackPatientSearch, setFeedbackPatientSearch] = useState('');
  const [feedbackPatientResults, setFeedbackPatientResults] = useState([]);
  const [sendingFeedback, setSendingFeedback] = useState(null); // patientId being sent
  const [feedbackMsg, setFeedbackMsg] = useState(null); // { type: 'success'|'error', text }

  const searchPatientsForFeedback = async () => {
    if (!feedbackPatientSearch.trim() || !practiceId) return;
    try {
      const res = await api.getPatients(practiceId, { search: feedbackPatientSearch.trim(), limit: 5 });
      setFeedbackPatientResults(res.success ? (res.data || []) : []);
    } catch { setFeedbackPatientResults([]); }
  };

  const handleSendFeedback = async (patientId) => {
    setSendingFeedback(patientId);
    setFeedbackMsg(null);
    try {
      const res = await api.requestPatientFeedback(practiceId, patientId, null, true);
      if (res.success) {
        setFeedbackMsg({ type: 'success', text: 'Feedback request sent successfully' });
      } else {
        setFeedbackMsg({ type: 'error', text: res.error || 'Failed to send' });
      }
    } catch (err) {
      setFeedbackMsg({ type: 'error', text: err.message || 'Failed to send feedback request' });
    } finally {
      setSendingFeedback(null);
    }
  };

  const handleGenerateResponse = async (feedbackId, tone = 'empathetic') => {
    setAiDraft({ feedbackId, response: null, tone, loading: true, editing: false });
    try {
      const result = await api.generateAiReviewResponse(practiceId, feedbackId, tone);
      if (result.success && result.data) {
        setAiDraft({
          feedbackId,
          response: result.data.draft_response || result.data.response,
          responseId: result.data.id,
          tone,
          loading: false,
          editing: false,
        });
        setEditedResponse(result.data.draft_response || result.data.response || '');
      } else {
        setAiDraft(null);
      }
    } catch (err) {
      console.error('Error generating AI response:', err);
      alert('Failed to generate AI response');
      setAiDraft(null);
    }
  };

  const handleRegenerateTone = async (tone) => {
    if (!aiDraft) return;
    handleGenerateResponse(aiDraft.feedbackId, tone);
  };

  const handleApproveResponse = async () => {
    if (!aiDraft?.responseId) return;
    try {
      await api.updateAiReviewResponse(practiceId, aiDraft.responseId, {
        status: 'approved',
        edited_response: editedResponse !== aiDraft.response ? editedResponse : undefined,
      });
      alert('Response approved and ready to send.');
      setAiDraft(null);
    } catch (err) {
      console.error('Error approving response:', err);
      alert('Failed to approve response');
    }
  };

  return (
    <div>
      {/* Manual Feedback Trigger */}
      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: '#0369a1' }}>Send Feedback Request</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search patient by name..."
            value={feedbackPatientSearch}
            onChange={(e) => setFeedbackPatientSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchPatientsForFeedback()}
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, minWidth: 220 }}
          />
          <button onClick={searchPatientsForFeedback} style={{ padding: '6px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
            Search
          </button>
        </div>
        {feedbackPatientResults.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {feedbackPatientResults.map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: 13 }}>{p.first_name} {p.last_name} {p.phone ? `(${p.phone})` : ''} {p.email ? `(${p.email})` : ''}</span>
                <button
                  onClick={() => handleSendFeedback(p.id)}
                  disabled={sendingFeedback === p.id}
                  style={{ padding: '4px 12px', backgroundColor: '#059669', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer', opacity: sendingFeedback === p.id ? 0.6 : 1 }}
                >
                  {sendingFeedback === p.id ? 'Sending...' : 'Send Feedback Request'}
                </button>
              </div>
            ))}
          </div>
        )}
        {feedbackMsg && (
          <div style={{ marginTop: 8, fontSize: 13, color: feedbackMsg.type === 'success' ? '#059669' : '#dc2626' }}>
            {feedbackMsg.text}
          </div>
        )}
      </div>

      <div style={styles.filterBar}>
        <select
          value={filter}
          onChange={(e) => onFilterChange(e.target.value)}
          style={styles.select}
        >
          <option value="ALL">All Feedback</option>
          <option value="NEEDS_REVIEW">Needs Review</option>
          <option value="REVIEWED">Reviewed</option>
          <option value="RESOLVED">Resolved</option>
          <option value="LOW_RATINGS">Low Ratings Only</option>
        </select>
      </div>

      {feedback.length === 0 ? (
        <div style={styles.emptyState}>No feedback found</div>
      ) : (
        <div style={styles.feedbackList}>
          {feedback.map(item => (
            <div key={item.id} style={styles.feedbackCard}>
              <div style={styles.feedbackHeader}>
                <div style={styles.feedbackRating}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <span
                      key={star}
                      style={{
                        color: star <= item.rating ? '#fbbf24' : '#d1d5db',
                        fontSize: '18px'
                      }}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <span
                  style={{
                    ...styles.statusBadge,
                    backgroundColor: statusColors[item.review_status] + '20',
                    color: statusColors[item.review_status]
                  }}
                >
                  {item.review_status}
                </span>
              </div>
              {item.comment && (
                <p style={styles.feedbackComment}>"{item.comment}"</p>
              )}
              <div style={styles.feedbackMeta}>
                <span>{item.first_name} {item.last_name}</span>
                <span>{new Date(item.created_at).toLocaleDateString()}</span>
              </div>

              {/* AI Draft Panel */}
              {aiDraft && aiDraft.feedbackId === item.id && (
                <div style={aiStyles.draftPanel}>
                  {aiDraft.loading ? (
                    <div style={aiStyles.draftLoading}>
                      <div style={aiStyles.spinner} />
                      <span>Generating {aiDraft.tone} response...</span>
                    </div>
                  ) : (
                    <>
                      <div style={aiStyles.draftHeader}>
                        <span style={aiStyles.draftLabel}>AI Draft ({aiDraft.tone})</span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {['empathetic', 'professional', 'apologetic'].map(t => (
                            <button
                              key={t}
                              onClick={() => handleRegenerateTone(t)}
                              style={{
                                ...aiStyles.toneButton,
                                ...(t === aiDraft.tone ? aiStyles.toneButtonActive : {}),
                              }}
                            >
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                      {aiDraft.editing ? (
                        <textarea
                          value={editedResponse}
                          onChange={(e) => setEditedResponse(e.target.value)}
                          style={aiStyles.draftTextarea}
                          rows={4}
                        />
                      ) : (
                        <div style={aiStyles.draftText}>{editedResponse}</div>
                      )}
                      <div style={aiStyles.draftActions}>
                        <button
                          onClick={() => setAiDraft({ ...aiDraft, editing: !aiDraft.editing })}
                          style={aiStyles.editButton}
                        >
                          {aiDraft.editing ? 'Preview' : 'Edit'}
                        </button>
                        <button onClick={handleApproveResponse} style={aiStyles.approveButton}>
                          Approve
                        </button>
                        <button onClick={() => setAiDraft(null)} style={aiStyles.rejectButton}>
                          Discard
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div style={styles.feedbackActions}>
                {/* AI Generate button for low ratings */}
                {item.rating <= 3 && item.comment && !(aiDraft && aiDraft.feedbackId === item.id) && (
                  <button
                    onClick={() => handleGenerateResponse(item.id)}
                    style={aiStyles.generateButton}
                  >
                    &#9733; AI Response
                  </button>
                )}
                {item.review_status === 'NEEDS_REVIEW' && (
                  <>
                    <button
                      onClick={() => onUpdateStatus(item.id, 'REVIEWED')}
                      style={styles.actionButton}
                    >
                      Mark Reviewed
                    </button>
                    <button
                      onClick={() => onUpdateStatus(item.id, 'RESOLVED')}
                      style={{ ...styles.actionButton, backgroundColor: '#10b981' }}
                    >
                      Mark Resolved
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const aiStyles = {
  generateButton: {
    padding: '6px 14px',
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  draftPanel: {
    marginTop: '12px',
    padding: '14px',
    background: '#f8f7ff',
    border: '1px solid #c7d2fe',
    borderRadius: '8px',
  },
  draftLoading: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#667eea',
    fontSize: '13px',
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #c7d2fe',
    borderTopColor: '#667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  draftHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  draftLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#667eea',
  },
  toneButton: {
    padding: '3px 8px',
    fontSize: '11px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    background: '#fff',
    color: '#6b7280',
    cursor: 'pointer',
  },
  toneButtonActive: {
    background: '#667eea',
    color: '#fff',
    borderColor: '#667eea',
  },
  draftText: {
    fontSize: '13px',
    lineHeight: '1.6',
    color: '#374151',
    whiteSpace: 'pre-wrap',
    padding: '10px',
    background: '#fff',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  draftTextarea: {
    width: '100%',
    fontSize: '13px',
    lineHeight: '1.6',
    color: '#374151',
    padding: '10px',
    border: '1px solid #c7d2fe',
    borderRadius: '6px',
    resize: 'vertical',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },
  draftActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '10px',
  },
  editButton: {
    padding: '5px 12px',
    fontSize: '12px',
    border: '1px solid #d1d5db',
    borderRadius: '5px',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
  },
  approveButton: {
    padding: '5px 14px',
    fontSize: '12px',
    border: 'none',
    borderRadius: '5px',
    background: '#10b981',
    color: '#fff',
    fontWeight: '600',
    cursor: 'pointer',
  },
  rejectButton: {
    padding: '5px 12px',
    fontSize: '12px',
    border: '1px solid #fecaca',
    borderRadius: '5px',
    background: '#fff',
    color: '#dc2626',
    cursor: 'pointer',
  },
};

// Locations Tab Component
function LocationsTab({ locations, weightValid, onAdd, onEdit, onDelete, onRefresh, practiceId, editing, onCancelEdit }) {
  const isNew = editing === 'new';
  const [form, setForm] = useState({ name: '', address: '', weight_percentage: 0, google_review_url: '', enabled: 1 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing && editing !== 'new') {
      setForm({ name: editing.name || '', address: editing.address || '', weight_percentage: editing.weight_percentage || 0, google_review_url: editing.google_review_url || '', enabled: editing.enabled ?? 1 });
    } else if (editing === 'new') {
      setForm({ name: '', address: '', weight_percentage: 0, google_review_url: '', enabled: 1 });
    }
  }, [editing]);

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    try {
      setSaving(true);
      if (isNew) {
        await api.createLocation(practiceId, form);
      } else {
        await api.updateLocation(practiceId, editing.id, form);
      }
      onCancelEdit();
      onRefresh();
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={styles.headerRow}>
        <button onClick={onAdd} style={styles.primaryButton}>Add Location</button>
        {!weightValid && (
          <span style={styles.warningBadge}>Weights must sum to 100%</span>
        )}
      </div>

      {editing && (
        <div style={{ padding: 16, marginBottom: 16, border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#f9fafb' }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>{isNew ? 'Add Location' : 'Edit Location'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={styles.formLabel}>Name *</label>
              <input style={styles.formInput} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Main Office" />
            </div>
            <div>
              <label style={styles.formLabel}>Address</label>
              <input style={styles.formInput} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="123 Main St, City, ST" />
            </div>
            <div>
              <label style={styles.formLabel}>Weight %</label>
              <input type="number" style={styles.formInput} value={form.weight_percentage} onChange={e => setForm({ ...form, weight_percentage: parseInt(e.target.value) || 0 })} min={0} max={100} />
            </div>
            <div>
              <label style={styles.formLabel}>Google Review URL</label>
              <input style={styles.formInput} value={form.google_review_url} onChange={e => setForm({ ...form, google_review_url: e.target.value })} placeholder="https://g.page/..." />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0', fontSize: 13 }}>
            <input type="checkbox" checked={!!form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked ? 1 : 0 })} /> Enabled
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onCancelEdit} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={styles.primaryButton}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      )}

      {locations.length === 0 && !editing ? (
        <div style={styles.emptyState}>No locations configured. Add your first location to start distributing reviews.</div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Address</th>
                <th style={styles.th}>Weight</th>
                <th style={styles.th}>Google URL</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.map(loc => (
                <tr key={loc.id}>
                  <td style={styles.td}>{loc.name}</td>
                  <td style={styles.td}>{loc.address || '-'}</td>
                  <td style={styles.td}>{loc.weight_percentage}%</td>
                  <td style={styles.td}>
                    {loc.google_review_url ? (
                      <a href={loc.google_review_url} target="_blank" rel="noopener noreferrer">View</a>
                    ) : '-'}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusDot,
                      backgroundColor: loc.enabled ? '#10b981' : '#9ca3af'
                    }} />
                    {loc.enabled ? 'Enabled' : 'Disabled'}
                  </td>
                  <td style={styles.td}>
                    <button onClick={() => onEdit(loc)} style={styles.linkButton}>Edit</button>
                    <button onClick={() => onDelete(loc.id)} style={styles.linkButtonDanger}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Platforms Tab Component
function PlatformsTab({ platforms, weightValid, onAdd, onEdit, onDelete, onRefresh, practiceId, editing, onCancelEdit }) {
  const PLATFORM_OPTIONS = ['Google', 'Yelp', 'Healthgrades', 'Zocdoc', 'Vitals', 'WebMD', 'Facebook', 'Other'];
  const isNew = editing === 'new';
  const [form, setForm] = useState({ platform: 'Google', url: '', weight_percentage: 0, enabled: 1, department_id: '' });
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    if (!practiceId) return;
    api.getDepartments(practiceId)
      .then(res => setDepartments(res?.data || res?.departments || []))
      .catch(() => setDepartments([]));
  }, [practiceId]);

  useEffect(() => {
    if (editing && editing !== 'new') {
      setForm({
        platform: editing.platform || 'Google',
        url: editing.url || '',
        weight_percentage: editing.weight_percentage || 0,
        enabled: editing.enabled ?? 1,
        department_id: editing.department_id || ''
      });
    } else if (editing === 'new') {
      setForm({ platform: 'Google', url: '', weight_percentage: 0, enabled: 1, department_id: '' });
    }
  }, [editing]);

  const handleSave = async () => {
    if (!form.url.trim()) { alert('URL is required'); return; }
    try {
      setSaving(true);
      const payload = {
        ...form,
        platform: form.platform.toUpperCase(),
        department_id: form.department_id ? parseInt(form.department_id, 10) : null
      };
      if (isNew) {
        await api.createPlatform(practiceId, payload);
      } else {
        await api.updatePlatform(practiceId, editing.id, payload);
      }
      onCancelEdit();
      onRefresh();
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const departmentName = (id) => {
    if (!id) return 'Practice-wide';
    const dept = departments.find(d => d.id === id);
    return dept ? dept.name : `Dept #${id}`;
  };

  return (
    <div>
      <div style={styles.headerRow}>
        <button onClick={onAdd} style={styles.primaryButton}>Add Platform</button>
        {!weightValid && (
          <span style={styles.warningBadge}>Weights must sum to 100%</span>
        )}
      </div>

      {editing && (
        <div style={{ padding: 16, marginBottom: 16, border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: '#f9fafb' }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>{isNew ? 'Add Platform' : 'Edit Platform'}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={styles.formLabel}>Platform *</label>
              <select style={styles.formInput} value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}>
                {PLATFORM_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={styles.formLabel}>Weight %</label>
              <input type="number" style={styles.formInput} value={form.weight_percentage} onChange={e => setForm({ ...form, weight_percentage: parseInt(e.target.value) || 0 })} min={0} max={100} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={styles.formLabel}>Review URL *</label>
            <input style={styles.formInput} value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://www.google.com/maps/place/..." />
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={styles.formLabel}>
              Department <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional — leave empty for practice-wide)</span>
            </label>
            <select
              style={styles.formInput}
              value={form.department_id || ''}
              onChange={e => setForm({ ...form, department_id: e.target.value })}
            >
              <option value="">Practice-wide (all departments)</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Department-specific URLs override practice-wide ones when the patient's appointment matches.
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0', fontSize: 13 }}>
            <input type="checkbox" checked={!!form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked ? 1 : 0 })} /> Enabled
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={onCancelEdit} style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={styles.primaryButton}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      )}

      {platforms.length === 0 && !editing ? (
        <div style={styles.emptyState}>No platforms configured. Add review platforms to distribute reviews.</div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Platform</th>
                <th style={styles.th}>Department</th>
                <th style={styles.th}>Weight</th>
                <th style={styles.th}>URL</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {platforms.map(plat => (
                <tr key={plat.id}>
                  <td style={styles.td}>{plat.platform}</td>
                  <td style={{ ...styles.td, color: plat.department_id ? '#111827' : '#9ca3af' }}>
                    {departmentName(plat.department_id)}
                  </td>
                  <td style={styles.td}>{plat.weight_percentage}%</td>
                  <td style={styles.td}>
                    {plat.url ? (
                      <a href={plat.url} target="_blank" rel="noopener noreferrer" style={styles.truncatedLink}>
                        {plat.url.substring(0, 40)}{plat.url.length > 40 ? '...' : ''}
                      </a>
                    ) : '-'}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusDot,
                      backgroundColor: plat.enabled ? '#10b981' : '#9ca3af'
                    }} />
                    {plat.enabled ? 'Enabled' : 'Disabled'}
                  </td>
                  <td style={styles.td}>
                    <button onClick={() => onEdit(plat)} style={styles.linkButton}>Edit</button>
                    <button onClick={() => onDelete(plat.id)} style={styles.linkButtonDanger}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Settings Tab Component
function SettingsTab({ config, onChange, onSave, saving }) {
  if (!config) {
    return <div style={styles.loading}>Loading settings...</div>;
  }

  const handleChange = (field, value) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div style={styles.settingsForm}>
      <div style={styles.formGroup}>
        <label style={styles.label}>
          <input
            type="checkbox"
            checked={config.feedback_enabled === 1}
            onChange={(e) => handleChange('feedback_enabled', e.target.checked ? 1 : 0)}
            style={styles.checkbox}
          />
          Feedback Collection Enabled
        </label>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Feedback Channels <Tooltip text="How feedback requests are sent to patients. SMS sends a text with the feedback link; Email sends a branded email." /></label>
        <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
          {['SMS', 'EMAIL'].map(ch => {
            const channels = (() => { try { return JSON.parse(config.channels_enabled || '[]'); } catch { return []; } })();
            const isChecked = channels.includes(ch);
            return (
              <label key={ch} style={{ ...styles.label, fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    const updated = e.target.checked
                      ? [...channels, ch]
                      : channels.filter(c => c !== ch);
                    if (updated.length === 0) return; // must have at least one channel
                    handleChange('channels_enabled', JSON.stringify(updated));
                  }}
                  style={styles.checkbox}
                />
                {ch === 'SMS' ? 'SMS' : 'Email'}
              </label>
            );
          })}
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>High Rating Threshold <Tooltip text="Minimum star rating to route patients to public review sites" /></label>
        <select
          value={config.high_rating_threshold}
          onChange={(e) => handleChange('high_rating_threshold', parseInt(e.target.value))}
          style={styles.select}
        >
          <option value={4}>4+ stars</option>
          <option value={5}>5 stars only</option>
        </select>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Feedback Delay <Tooltip text="Hours to wait after a visit before sending feedback request" /></label>
        <input
          type="number"
          value={config.feedback_delay_hours}
          onChange={(e) => handleChange('feedback_delay_hours', parseInt(e.target.value))}
          style={styles.input}
          min={0}
          max={72}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Cooloff Period <Tooltip text="Minimum days between feedback requests for the same patient" /></label>
        <input
          type="number"
          value={config.cooloff_period_days}
          onChange={(e) => handleChange('cooloff_period_days', parseInt(e.target.value))}
          style={styles.input}
          min={1}
          max={90}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Max Reminder Attempts <Tooltip text="Maximum number of reminder messages to send for feedback" /></label>
        <input
          type="number"
          value={config.max_reminder_attempts}
          onChange={(e) => handleChange('max_reminder_attempts', parseInt(e.target.value))}
          style={styles.input}
          min={0}
          max={5}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Token Expiry <Tooltip text="How long the feedback link remains valid" /></label>
        <input
          type="number"
          value={config.token_expiry_hours}
          onChange={(e) => handleChange('token_expiry_hours', parseInt(e.target.value))}
          style={styles.input}
          min={24}
          max={720}
        />
      </div>

      <FeedbackTemplatesSection config={config} onChange={handleChange} />

      <button
        onClick={onSave}
        disabled={saving}
        style={styles.primaryButton}
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
}

// commons #252: practice-editable feedback message templates. Mirrors
// FEEDBACK_TEMPLATE_DEFAULTS in src/services/FeedbackService.js — the server
// is the source of truth; these copies only fill the placeholder text and the
// preview when a field is blank.
const FEEDBACK_TEMPLATE_DEFAULTS = {
  feedback_sms_template: 'Hi {{patient_first_name}}, thanks for visiting {{practice_name}}! How was your experience? Share your feedback: {{feedback_link}} Reply STOP to unsubscribe.',
  feedback_email_subject: 'How was your visit to {{practice_name}}?',
  feedback_email_body: "Hi {{patient_first_name}},\n\nThank you for choosing {{practice_name}}. We hope your recent visit met your expectations.\n\nWe'd love to hear about your experience - it only takes a moment:\n\n{{feedback_link}}\n\nYour feedback helps us serve you better.\n\nBest regards,\n{{practice_name}} Team",
  feedback_reminder_sms_template: "Hi {{patient_first_name}}, we're still hoping to hear about your recent visit to {{practice_name}}. Share your thoughts: {{feedback_link}}",
};

const FEEDBACK_TEMPLATE_FIELDS = [
  { key: 'feedback_sms_template', label: 'Feedback SMS', rows: 3, needsLink: true },
  { key: 'feedback_email_subject', label: 'Feedback email subject', rows: 1, needsLink: false },
  { key: 'feedback_email_body', label: 'Feedback email body', rows: 8, needsLink: true, help: 'Plain text. A line containing only {{feedback_link}} becomes the "Share Your Feedback" button; blank lines separate paragraphs. The unsubscribe footer is always added.' },
  { key: 'feedback_reminder_sms_template', label: 'Reminder SMS', rows: 3, needsLink: true },
];

const PREVIEW_VARS = {
  patient_first_name: 'Jane',
  practice_name: '(practice or brand name)',
  feedback_link: 'https://connect.newtonhealthcore.com/s/abc123',
  unsubscribe_link: 'https://connect.newtonhealthcore.com/unsubscribe/feedback/…',
};

function renderPreview(template, vars) {
  return String(template).replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (m, name) => (vars[name] != null ? vars[name] : m));
}

function FeedbackTemplatesSection({ config, onChange }) {
  const [previewKey, setPreviewKey] = useState(null);

  return (
    <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 8, paddingTop: 20, marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Message templates</h3>
      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
        Wording of the feedback request messages. Leave a field blank to use the built-in default.
        Placeholders: {['patient_first_name', 'practice_name', 'feedback_link', 'unsubscribe_link'].map(v => (
          <code key={v} style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 4, marginRight: 6 }}>{`{{${v}}}`}</code>
        ))}
        <span>{'{{practice_name}}'} is the brand for the appointment (e.g. a Brand Override), not always the practice name.</span>
      </p>

      {FEEDBACK_TEMPLATE_FIELDS.map(f => {
        const value = config[f.key] || '';
        const effective = value.trim() || FEEDBACK_TEMPLATE_DEFAULTS[f.key];
        const missingLink = f.needsLink && value.trim() && !value.includes('{{feedback_link}}');
        return (
          <div style={styles.formGroup} key={f.key}>
            <label style={styles.label}>
              {f.label}
              {!value.trim() && <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8 }}>(using default)</span>}
            </label>
            <textarea
              rows={f.rows}
              value={value}
              placeholder={FEEDBACK_TEMPLATE_DEFAULTS[f.key]}
              onChange={(e) => onChange(f.key, e.target.value)}
              style={{ ...styles.input, fontFamily: 'inherit', resize: 'vertical', borderColor: missingLink ? '#ef4444' : '#d1d5db' }}
            />
            {f.help && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{f.help}</div>}
            {missingLink && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>Must include {'{{feedback_link}}'} — patients need the link.</div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12 }}>
              <button type="button" onClick={() => setPreviewKey(previewKey === f.key ? null : f.key)} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0 }}>
                {previewKey === f.key ? 'Hide preview' : 'Preview'}
              </button>
              {value.trim() && (
                <button type="button" onClick={() => onChange(f.key, '')} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0 }}>
                  Reset to default
                </button>
              )}
              {(f.key === 'feedback_sms_template' || f.key === 'feedback_reminder_sms_template') && (
                <span style={{ color: effective.length > 320 ? '#b45309' : '#9ca3af' }}>{effective.length} chars (before link shortening)</span>
              )}
            </div>
            {previewKey === f.key && (
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, marginTop: 6 }}>
                {renderPreview(effective, PREVIEW_VARS)}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  container: { padding: '0' },
  pageTitle: { fontSize: '24px', fontWeight: '600', marginBottom: '20px', color: '#1f2937' },
  tabs: { display: 'flex', gap: '4px', borderBottom: '1px solid #e5e7eb', marginBottom: '20px' },
  tab: {
    padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer',
    fontSize: '14px', fontWeight: '500', color: '#6b7280', borderBottom: '2px solid transparent'
  },
  activeTab: { color: '#2563eb', borderBottomColor: '#2563eb' },
  tabContent: { },
  loading: { textAlign: 'center', padding: '40px', color: '#6b7280' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '8px' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '24px' },
  summaryCard: { backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' },
  summaryValue: { fontSize: '32px', fontWeight: '600', color: '#1f2937' },
  summaryLabel: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  section: { marginBottom: '24px' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#1f2937' },
  ratingBars: { display: 'flex', flexDirection: 'column', gap: '8px' },
  ratingBar: { display: 'flex', alignItems: 'center', gap: '12px' },
  ratingLabel: { width: '50px', fontSize: '14px', color: '#6b7280' },
  barContainer: { flex: 1, height: '20px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' },
  barFill: { height: '100%', transition: 'width 0.3s' },
  ratingCount: { width: '40px', textAlign: 'right', fontSize: '14px', color: '#6b7280' },
  alertBox: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '24px' },
  distributionGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' },
  distributionCard: { backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  distributionTitle: { fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#1f2937' },
  distributionItem: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: '14px' },
  filterBar: { marginBottom: '16px' },
  select: { padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' },
  feedbackList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  feedbackCard: { backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  feedbackHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  feedbackRating: { display: 'flex', gap: '2px' },
  statusBadge: { padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' },
  feedbackComment: { color: '#4b5563', fontStyle: 'italic', margin: '8px 0' },
  feedbackMeta: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#9ca3af' },
  feedbackActions: { display: 'flex', gap: '8px', marginTop: '12px' },
  actionButton: { padding: '6px 12px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  primaryButton: { padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  warningBadge: { backgroundColor: '#fef3c7', color: '#d97706', padding: '6px 12px', borderRadius: '4px', fontSize: '12px' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  th: { textAlign: 'left', padding: '12px 16px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' },
  td: { padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontSize: '14px' },
  statusDot: { display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', marginRight: '6px' },
  linkButton: { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '14px', marginRight: '8px' },
  linkButtonDanger: { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '14px' },
  truncatedLink: { color: '#2563eb', textDecoration: 'none', fontSize: '12px' },
  settingsForm: { maxWidth: '600px' },
  formGroup: { marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' },
  checkbox: { marginRight: '8px' },
  controlBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px', flexWrap: 'wrap', gap: '12px'
  },
  buttonGroup: { display: 'flex', gap: '4px' },
  rangeButton: {
    padding: '8px 16px', border: '1px solid #d1d5db', background: 'white',
    borderRadius: '6px', fontSize: '13px', cursor: 'pointer', color: '#374151',
    fontWeight: '500'
  },
  rangeButtonActive: {
    backgroundColor: '#2563eb', color: 'white', borderColor: '#2563eb'
  },
  chartCard: {
    backgroundColor: 'white', padding: '20px', borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px'
  },
  formLabel: { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 },
  formInput: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }
};

// Inject spinner animation
if (typeof document !== 'undefined' && !document.getElementById('reputation-ai-styles')) {
  const s = document.createElement('style');
  s.id = 'reputation-ai-styles';
  s.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

export default Reputation;
